// No icon imports here: jest's resolver ignores the design package's `exports` subpaths, so pulling
// lucide in would break every test that touches these constants. Icons live with the components.
import { recommendedEquipmentLabels } from '@/constants/recommendedEquipmentLabels';
import { LOCATION_TYPES } from '@/constants/locationTypes';

export const WEEKDAY_LABELS = {
    sunday: 'Sun',
    monday: 'Mon',
    tuesday: 'Tue',
    wednesday: 'Wed',
    thursday: 'Thu',
    friday: 'Fri',
    saturday: 'Sat',
};

export const UPDATE_TYPES = {
    SPLIT: 'SPLIT',
    OVERWRITE: 'OVERWRITE',
    DELETE_RESOURCE: 'DELETE_RESOURCE',
};

// The Save question: which days a schedule change covers. Only the schedule — everything on the
// `resources` row (name, ceilings, vehicle, timeframes) is shared by every day it runs, so Save
// always applies those everywhere.
export const UPDATE_TYPE_TITLES = {
    [UPDATE_TYPES.SPLIT]: {
        title: 'From the start date on',
        subtitle:
            'Keeps the days already run. The new schedule takes over on its start date, or today if that has passed or it does not repeat',
    },
    [UPDATE_TYPES.OVERWRITE]: {
        title: 'The whole schedule, past included',
        subtitle: 'Replaces the schedule on every day, including days already run',
    },
};

// The Delete question: how much of the resource goes.
export const DELETE_TYPE_TITLES = {
    [UPDATE_TYPES.SPLIT]: {
        title: 'End the schedule today',
        subtitle: 'Keeps the days already run. Nothing is scheduled from today on',
    },
    [UPDATE_TYPES.OVERWRITE]: {
        title: 'Remove the whole schedule',
        subtitle: 'Takes the schedule off every day, including days already run',
    },
    [UPDATE_TYPES.DELETE_RESOURCE]: {
        title: 'Delete entire resource',
        subtitle: 'Completely erase. Cannot be undone',
    },
};

// Values must stay in step with the resources_vehicle_type_check constraint (ENG-6065).
export const VEHICLE_TYPES = [
    { value: 'box_truck', label: 'Box truck' },
    { value: 'pickup_truck', label: 'Pickup truck' },
    { value: 'cargo_van', label: 'Cargo van' },
    { value: 'sprinter_van', label: 'Sprinter van' },
    { value: 'flatbed', label: 'Flatbed' },
    { value: 'other', label: 'Other' },
];

// `resources.equipment` deliberately shares its key vocabulary with
// `orders.recommended_equipment` / `orders.equipment_overrides`, so what a resource carries can be
// compared against what an order asks for. Sourced from the one label map rather than re-listed
// here, so the two can't drift.
export const EQUIPMENT_TYPES = Object.entries(recommendedEquipmentLabels).map(([value, label]) => ({
    value,
    label,
}));

// The page's two views. Calendar is the day-by-day schedule; Fleet is every resource as a list,
// and the only place one is created or configured.
export const PRIMARY_TABS = {
    CALENDAR: 'calendar',
    FLEET: 'fleet',
};

export const STEPS = {
    RESOURCE: 'RESOURCE',
    UPDATE: 'UPDATE',
    DELETE: 'DELETE',
};

// Each value is the suffix of the capacity_* column it measures. Not "primary" anything — there is
// no stored primary_ceiling, and no single capacity dimension: every ceiling is checked on its own,
// so readers derive whichever of these matters at read time.
export const CEILINGS = [
    { value: 'cubes', label: 'Cubic ft', unit: 'cu ft/day', short: 'cu ft', column: 'capacity_cubes' },
    { value: 'weight', label: 'Weight', unit: 'lbs/day', short: 'lbs', column: 'capacity_weight' },
    { value: 'stops', label: '# of stops', unit: 'per day', short: 'stops', column: 'capacity_stops' },
    { value: 'items', label: 'Pieces', unit: 'per day', short: 'pieces', column: 'capacity_items' },
];

export const CEILINGS_BY_VALUE = Object.fromEntries(CEILINGS.map((c) => [c.value, c]));

// `resources.service_types` holds `orders.dropoff_location_type` keys, so what a resource can
// service can be compared against what an order asks for — the same relationship `equipment` has
// with `recommended_equipment`. Sourced from the one label map rather than re-listed here, so the
// two can't drift, and the values stay in step with resources_service_types_check (ENG-6065).
//
// Deliberately not the per-client `service_levels` rows, which the Service Type column also draws
// on: those are uuids, which a text[] of these seven cannot carry a CHECK alongside. An order on a
// custom service level still populates dropoff_location_type, so it is matched on that.
export const SERVICE_TYPES = Object.entries(LOCATION_TYPES).map(([value, label]) => ({ value, label }));
